/**
 * @file PublishOnSiteRadios.tsx
 * @description «Показ на сайте» товара (phase14.0.2; phase19.31 — подписи
 * словами вместо символов «↳ ✓ ✗», текст без «Override… дефолт»).
 *
 * Три значения (catalog-taxonomy.md «Логика публикации»): как в категории
 * (по умолчанию), показывать, не показывать. Своё значение действует только
 * для этого товара. Переключатель — группа радиокнопок с целью касания по
 * теме (48 до 1024, 44 с 1024).
 */

import { Label } from "../../../../components/ui/label";
import { cn } from "@/lib/utils";
import type { PublishOnSiteRadio } from "../product-form";

interface Props {
  value: PublishOnSiteRadio;
  /** Действующая публикация от категории; null — категория не выбрана. */
  inherited: boolean | null;
  onChange: (value: PublishOnSiteRadio) => void;
  disabled?: boolean;
}

/** Варианты — данными. */
const OPTIONS: Array<{ value: PublishOnSiteRadio; label: string }> = [
  { value: "inherit", label: "Как в категории" },
  { value: "true", label: "Показывать на сайте" },
  { value: "false", label: "Не показывать на сайте" },
];

export function PublishOnSiteRadios({ value, inherited, onChange, disabled }: Props) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label id="p-publish-label">Показ на сайте</Label>
      <div role="radiogroup" aria-labelledby="p-publish-label" className="flex flex-col gap-1">
        {OPTIONS.map((opt) => {
          const checked = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => onChange(opt.value)}
              disabled={disabled}
              className={cn(
                "type-body flex min-h-[var(--control-h,3rem)] items-center gap-3 rounded-md px-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60",
                checked ? "bg-accent font-medium text-on-surface" : "text-on-surface hover:bg-surface-low",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border-2",
                  checked ? "border-primary" : "border-outline-variant",
                )}
              >
                {checked ? <span className="size-2 rounded-full bg-primary" /> : null}
              </span>
              <span className="flex-1">
                {opt.label}
                {opt.value === "inherit" && inherited !== null ? (
                  <span className="text-on-surface-variant"> — сейчас {inherited ? "показывается" : "не показывается"}</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
      <p className="type-caption text-on-surface-variant">
        Своё значение действует только для этого товара; по умолчанию — как в категории
      </p>
    </div>
  );
}
